import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Flight as FlightIcon,
  AttachMoney as MoneyIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SuperAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsers: 0,
    totalEvents: 0,
    totalTrips: 0,
    totalRevenue: 0,
    activeSubscriptions: 0
  });
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // In a real app, you'd verify that the current user has admin permissions here
        // For now, we'll skip that check for demonstration purposes
        
        // Fetch recent users
        const usersQuery = query(
          collection(db, 'users'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecentUsers(usersData);
        
        // Fetch recent events
        const eventsQuery = query(
          collection(db, 'events'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecentEvents(eventsData);
        
        // Set some placeholder statistics
        // In a real app, these would be calculated from actual data
        setStats({
          totalUsers: usersSnapshot.size + 95, // Just a placeholder value
          newUsers: 12,
          totalEvents: eventsSnapshot.size + 188, // Just a placeholder value
          totalTrips: 142,
          totalRevenue: 18750,
          activeSubscriptions: 75
        });
        
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError('Failed to load admin data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, navigate]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ p: 4, my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Super Admin Dashboard
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Users
                    </Typography>
                    <Typography variant="h3">
                      {stats.totalUsers.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Typography variant="body2">
                        New this month: {stats.newUsers}
                      </Typography>
                      <PersonIcon />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Revenue
                    </Typography>
                    <Typography variant="h3">
                      ${stats.totalRevenue.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Typography variant="body2">
                        Active subscriptions: {stats.activeSubscriptions}
                      </Typography>
                      <MoneyIcon />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Content
                    </Typography>
                    <Typography variant="h3">
                      {(stats.totalEvents + stats.totalTrips).toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Typography variant="body2">
                        Events: {stats.totalEvents} | Trips: {stats.totalTrips}
                      </Typography>
                      <EventIcon />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ width: '100%', mb: 4 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="admin dashboard tabs"
                >
                  <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
                  <Tab icon={<PersonIcon />} iconPosition="start" label="Users" />
                  <Tab icon={<EventIcon />} iconPosition="start" label="Events" />
                  <Tab icon={<MoneyIcon />} iconPosition="start" label="Financials" />
                  <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
                  <Tab icon={<EventIcon />} iconPosition="start" label="Event Management" onClick={() => navigate('/admin/event-management')} />
                </Tabs>
              </Box>
              
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Recent Users
                    </Typography>
                    <Paper sx={{ mb: 2 }}>
                      <List>
                        {recentUsers.map((user) => (
                          <ListItem 
                            key={user.id}
                            secondaryAction={
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => navigate(`/users/${user.id}`)}
                              >
                                View
                              </Button>
                            }
                            divider
                          >
                            <ListItemAvatar>
                              <Avatar src={user.photoURL}>
                                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={user.displayName || user.email || 'Unknown User'}
                              secondary={user.email || 'No email'}
                            />
                          </ListItem>
                        ))}
                      </List>
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button 
                          onClick={() => navigate('/users')}
                          size="small"
                        >
                          View All Users
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Recent Events
                    </Typography>
                    <Paper sx={{ mb: 2 }}>
                      <List>
                        {recentEvents.map((event) => (
                          <ListItem 
                            key={event.id}
                            secondaryAction={
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => navigate(`/events/${event.id}`)}
                              >
                                View
                              </Button>
                            }
                            divider
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                <EventIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={event.title || 'Unnamed Event'}
                              secondary={
                                event.startDate ? new Date(event.startDate.seconds * 1000).toLocaleDateString() : 'No date'
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button 
                          onClick={() => navigate('/events')}
                          size="small"
                        >
                          View All Events
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                      <Button 
                        variant="contained" 
                        fullWidth
                        startIcon={<PersonIcon />}
                        onClick={() => navigate('/users/invite')}
                        sx={{ p: 2 }}
                      >
                        Invite User
                      </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        fullWidth
                        startIcon={<EventIcon />}
                        onClick={() => navigate('/events/create')}
                        sx={{ p: 2 }}
                      >
                        Create Event
                      </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                      <Button 
                        variant="contained" 
                        color="success"
                        fullWidth
                        startIcon={<MoneyIcon />}
                        onClick={() => navigate('/admin/financial')}
                        sx={{ p: 2 }}
                      >
                        View Finances
                      </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                      <Button 
                        variant="contained" 
                        color="info"
                        fullWidth
                        startIcon={<DashboardIcon />}
                        onClick={() => navigate('/admin/dashboard')}
                        sx={{ p: 2 }}
                      >
                        Full Dashboard
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <Typography variant="h6" paragraph>
                  User Management
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/users')}
                  startIcon={<PersonIcon />}
                >
                  Go to User Management
                </Button>
              </TabPanel>
              
              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" paragraph>
                  Event Management
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => navigate('/events')}
                  startIcon={<EventIcon />}
                >
                  Go to Event Management
                </Button>
              </TabPanel>
              
              <TabPanel value={tabValue} index={3}>
                <Typography variant="h6" paragraph>
                  Financial Management
                </Typography>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={() => navigate('/admin/financial')}
                  startIcon={<MoneyIcon />}
                >
                  Go to Financial Dashboard
                </Button>
              </TabPanel>
              
              <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" paragraph>
                  System Settings
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  System settings page is under development and will be available soon.
                </Alert>
              </TabPanel>
              
              <TabPanel value={tabValue} index={5}>
                <Typography variant="h6" paragraph>
                  Event Management
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => navigate('/admin/event-management')}
                  startIcon={<EventIcon />}
                >
                  Go to Event Management
                </Button>
              </TabPanel>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SuperAdminPage; 